// IM-XPR-002 (IM-UX-112) — Páginas recomendadas para acompanhar: junta as recomendações que JÁ existem (fontes
// recomendadas, IM-UX-003, e sugestões de monitoramento do battlecard, IM-EXT-015), remove duplicidades pelo endereço,
// agrupa por empresa e ordena pela utilidade. Puro: não chama API, não ativa nada, não cria fonte nem sugestão nova.
// A ativação continua sendo feita pelas rotas donas (ativar recomendação ou monitorar pelo battlecard), com RBAC e
// auditoria delas. Rede social nunca entra nas recomendações principais: depende de autorização da rede.
import type { CompetitorMonitorSuggestionDto, SourceRecommendationDto } from '@intelimove/contracts';

export type PageOrigin =
  | { type: 'RECOMENDACAO'; id: string }
  | { type: 'BATTLECARD'; battlecardId: string; kind: CompetitorMonitorSuggestionDto['kind']; pageType: string | null };

export interface PageRecommendation {
  key: string;
  url: string;
  label: string;
  /** Empresa a que a página se refere ("Mercado e setor" quando não é de uma empresa). */
  company: string;
  pageType: string | null;
  reason: string;
  /** Impacto esperado de acompanhar esta página, em uma frase. */
  impact: string;
  /** Depende de autorização externa (rede social): nunca é ativada por aqui. */
  needsAuthorization: boolean;
  /** A última verificação não conseguiu abrir a página. */
  unavailable: boolean;
  score: number;
  origin: PageOrigin;
}

/** Chave de endereço para deduplicar: domínio sem `www`, caminho sem barra final, sem consulta e sem âncora. */
export function pageKey(url: string): string {
  try {
    const u = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`);
    const path = u.pathname.replace(/\/+$/, '') || '/';
    // IM-XPR-005: rede social regional/móvel (br.linkedin.com, m.facebook.com) é a mesma página da rede.
    const host = u.hostname.toLowerCase().replace(/^www\./, '').replace(/^(?:[a-z]{2}(?:-[a-z]{2})?|m|mobile|web)\.((?:linkedin|facebook|instagram|twitter|x|tiktok|youtube)\.com)$/, '$1');
    return `${host}${path.toLowerCase()}`;
  } catch {
    return url.trim().toLowerCase();
  }
}

const TYPE_WEIGHT: Record<string, number> = { PRECOS: 6, PRODUTOS: 5, SERVICOS: 5, IMPRENSA: 4, BLOG: 4, CASES: 3, CLIENTES: 3, VAGAS: 2, RI: 2, EVENTOS: 1, HOME: 1, DOCUMENTACAO: 1, OUTRA: 1 };
const IMPACT: Record<string, string> = {
  PRECOS: 'Mostra mudanças de preço, plano e pacote.', PRODUTOS: 'Mostra lançamentos e mudanças de oferta.', SERVICOS: 'Mostra lançamentos e mudanças de oferta.',
  IMPRENSA: 'Mostra anúncios, parcerias e mudanças de posicionamento.', BLOG: 'Mostra temas, lançamentos e posicionamento.', CASES: 'Mostra clientes conquistados e setores atendidos.',
  CLIENTES: 'Mostra clientes conquistados e setores atendidos.', VAGAS: 'Indica expansão de equipe e prioridades.', RI: 'Mostra resultados e movimentos societários.',
  EVENTOS: 'Mostra onde a empresa está presente.', HOME: 'Mostra mudanças de mensagem na página principal.', DOCUMENTACAO: 'Mostra mudanças no produto.',
};
export const impactOf = (pageType: string | null, feed: boolean) => (pageType && IMPACT[pageType]) ?? (feed ? 'Mostra publicações novas assim que saem.' : 'Mostra mudanças publicadas pela empresa.');

function scoreOf(x: { pageType: string | null; feed: boolean; competitor: boolean; available: boolean | null; reliability?: string }): number {
  let s = TYPE_WEIGHT[x.pageType ?? 'OUTRA'] ?? 1;
  if (x.feed) s += 2;
  if (x.competitor) s += 2;
  if (x.available === true) s += 2;
  if (x.reliability === 'ALTA') s += 1;
  return s;
}

export interface CompetitorSuggestions { competitorName: string; battlecardId: string; suggestions: readonly CompetitorMonitorSuggestionDto[] }

/**
 * Consolida e ordena. `monitored` = endereços já monitorados (não reaparecem). Em caso de duplicidade fica a recomendação
 * com verificação de disponibilidade (fonte recomendada) e o motivo mais completo.
 */
export function consolidatePages(recos: readonly SourceRecommendationDto[], competitors: readonly CompetitorSuggestions[], monitored: readonly string[] = []): PageRecommendation[] {
  const seen = new Set(monitored.filter(Boolean).map(pageKey));
  const out = new Map<string, PageRecommendation>();
  for (const r of recos) {
    if (r.status !== 'PENDING') continue;
    const key = pageKey(r.feedUrl ?? r.url);
    const alt = pageKey(r.url);
    if (seen.has(key) || seen.has(alt)) continue;
    const social = r.kind === 'social';
    const feed = r.kind === 'rss' || r.kind === 'atom' || r.kind === 'youtube';
    const item: PageRecommendation = {
      key, url: r.url, label: r.name, company: r.related?.name ?? (r.category === 'EMPRESA' ? 'Sua empresa' : 'Mercado e setor'), pageType: r.pageType, reason: r.reason,
      impact: impactOf(r.pageType, feed), needsAuthorization: social, unavailable: r.availability === 'INDISPONIVEL',
      score: scoreOf({ pageType: r.pageType, feed, competitor: r.category === 'CONCORRENTE', available: r.availability === 'DISPONIVEL' ? true : r.availability === 'INDISPONIVEL' ? false : null, reliability: r.reliability }),
      origin: { type: 'RECOMENDACAO', id: r.id },
    };
    out.set(key, item);
    if (alt !== key) seen.add(alt);
  }
  for (const c of competitors) {
    for (const s of c.suggestions) {
      const key = pageKey(s.url);
      if (seen.has(key)) continue;
      const prev = out.get(key);
      if (prev) { if (s.reason && !prev.reason.includes(s.reason)) prev.reason = `${prev.reason} ${s.reason}`.trim(); continue; }
      const feed = s.kind === 'feed' || s.kind === 'youtube';
      out.set(key, {
        key, url: s.url, label: s.label, company: c.competitorName, pageType: s.pageType, reason: s.reason, impact: impactOf(s.pageType, feed),
        needsAuthorization: s.kind === 'social', unavailable: false,
        score: scoreOf({ pageType: s.pageType, feed, competitor: true, available: null }),
        origin: { type: 'BATTLECARD', battlecardId: c.battlecardId, kind: s.kind, pageType: s.pageType },
      });
    }
  }
  return [...out.values()].sort((a, b) => b.score - a.score || a.company.localeCompare(b.company, 'pt-BR') || a.label.localeCompare(b.label, 'pt-BR'));
}

/**
 * As poucas páginas que valem ativar agora: no máximo `limit`, nunca rede social nem página indisponível, e no máximo uma
 * por empresa enquanto houver empresas diferentes (variedade antes de profundidade). O resto vai para "Ver outras".
 */
export function topPages(all: readonly PageRecommendation[], limit = 3): { top: PageRecommendation[]; rest: PageRecommendation[] } {
  const eligible = all.filter((x) => !x.needsAuthorization && !x.unavailable);
  const top: PageRecommendation[] = [];
  const companies = new Set<string>();
  for (const x of eligible) { if (top.length >= limit) break; if (!companies.has(x.company)) { top.push(x); companies.add(x.company); } }
  for (const x of eligible) { if (top.length >= limit) break; if (!top.includes(x)) top.push(x); }
  return { top, rest: all.filter((x) => !top.includes(x)) };
}

/** Agrupa por empresa, na ordem de utilidade da melhor página de cada uma. */
export function groupByCompany(items: readonly PageRecommendation[]): Array<{ company: string; items: PageRecommendation[] }> {
  const m = new Map<string, PageRecommendation[]>();
  for (const x of items) { const l = m.get(x.company) ?? []; l.push(x); m.set(x.company, l); }
  return [...m.entries()].map(([company, list]) => ({ company, items: list }));
}
